import React, { useContext } from 'react';
import * as Styled from './style';
import { ThemeContext } from '../../context/ThemeContext';
import './card.css';
import {
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
  Stack,
  Typography,
} from '@mui/material';
import KpiDefinitionDrawer from '../KpiDefinitionDrawer/KpiDefinitionDrawer';
import Loader from '../Loader/Loader';
import { helperFunctions } from '../../utils/helperFunctions';
import { color } from 'theme/Color';
import useCustomMediaQuery from '../../utils/useCustomMediaQuery';
import { screenSizes } from '../../constants/Constants';

const MetricsOverviewCard = ({
  metricConfig,
  showKpi = false,
  execView = false,
  isLoading = false,
  ppSubHeading = false,
  title,
  KpiDefinitionList = [],
  isDTC = false,
  istopkpi = true,
  noTextTransform = false,
}) => {
  const { extractNumberAndUnits } = helperFunctions();
  const { mode } = useContext(ThemeContext);
  const isMobileForOverviewCard = useCustomMediaQuery(screenSizes.mobile);

  const isPositive = (actual, target) => {
    const delta = Number(actual) - Number(target);
    if (delta >= 0) {
      return true; // actual is greater than target
    } else if (delta < 0) {
      return false; // actual is smaller than target
    }
  };

  const computeDelta = (actual, target) => {
    return Math.abs(Number(actual) - Number(target));
  };

  const theme = useTheme();
  // Question: should sm -> md be mobile size?
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:962px)');

  const renderKpiBlock = (kpiData) => {
    let titleClass = 'psd-kpiBlock';

    if (kpiData.title.length > 17) {
      if (isTablet) {
        titleClass += ' psd-longTitle';
      }
    }
    const definedValue = kpiData.valueParser(kpiData.value);
    const { numberPart, unitPart } = extractNumberAndUnits(definedValue);

    // Compute the className based on the conditions
    let subtitleClass;
    if (kpiData.valueParser(kpiData?.subtitle) === 'NA') {
      subtitleClass = 'psd-kpiStaticText';
    } else if (isPositive(kpiData.value, kpiData.subtitle)) {
      subtitleClass = `psd-greenBackground psd-${mode}Text`;
    } else {
      subtitleClass = `psd-redBackground psd-${mode}Text`;
    }

    return (
      <Styled.GridContainer
        container
        className={titleClass}
        data-testid="kpi-block"
        direction="column"
        mode={mode}
      >
        <Grid
          item
          className={`${istopkpi ? 'psd-kpiValue' : 'psd-kpiValue24'} psd-${mode}`}
        >
          {kpiData.valuePrefix && definedValue !== 'NA' && (
            <span
              className={`${istopkpi ? 'psd-kpiValue' : 'psd-kpiValue24'} psd-${mode}`}
            >
              {kpiData.valuePrefix}
            </span>
          )}
          {definedValue !== 'NA' ? numberPart : definedValue}
          {definedValue !== 'NA' && (
            <span className="psd-kpiUnit">{unitPart}</span>
          )}
          {kpiData.valueSuffix && definedValue !== 'NA' && (
            <span className="psd-kpiUnit">{kpiData.valueSuffix}</span>
          )}
        </Grid>
        <Grid
          item
          className={`${noTextTransform ? 'psd-kpiTitle-no-transform' : 'psd-kpiTitle'} psd-${mode}`}
        >
          {kpiData.title}
        </Grid>
        {kpiData.secondTitle ? (
          <Grid
            className={`${noTextTransform ? 'psd-kpiTitle-no-transform' : 'psd-kpiTitle'} psd-${mode}`}
          >
            {kpiData.secondTitle}
          </Grid>
        ) : null}
        {kpiData.checkPositive ? (
          <Grid className="psd-flexRow">
            <div className={`kpiSubtitle ${subtitleClass}`}>
              {kpiData.valueParser(
                computeDelta(kpiData.value, kpiData.subtitle)
              )}
            </div>
            <div className="kpiStaticText">vs Plan</div>
          </Grid>
        ) : null}
        {kpiData.subtitle && !kpiData.checkPositive ? (
          <Grid className="kpiSubtitle">{kpiData.subtitle}</Grid>
        ) : null}
      </Styled.GridContainer>
    );
  };
  return (
    <Styled.MainContainer mode={mode}>
      <Grid container direction={'column'} className="chartContainer">
        {execView && (
          <Grid item sx={{ padding: '10px' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                className="headerStyle"
                sx={{
                  display: 'contents',
                  color: '#EFEFEF',
                  fontSize: '18px',
                  fontFamily: 'Inter',
                  fontWeight: '700',
                }}
              >
                {title}
              </Typography>
              <KpiDefinitionDrawer
                title=""
                KpiDefinitionList={KpiDefinitionList}
              />
            </Stack>
          </Grid>
        )}
        {isLoading ? (
          <div style={{ padding: '40px' }}>
            <Loader />{' '}
          </div>
        ) : (
          <Grid
            item
            spacing={0}
            className="psd-flexRow"
            sx={{
              justifyContent: 'space-evenly',
              maxHeight: isDTC && isMobileForOverviewCard ? '125px' : 'auto',
            }}
          >
            {showKpi && !isMdUp && (
              <Grid xs={12} className="mobileDrawerIcon">
                <KpiDefinitionDrawer
                  title=""
                  ppSubHeadingNote={ppSubHeading}
                  KpiDefinitionList={KpiDefinitionList}
                />
              </Grid>
            )}
            {metricConfig?.map((metric, index) => (
              <React.Fragment key={`metric_fragment_${metric.title}`}>
                {isMobile && index > 0 && index % 2 !== 0 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      margin: '10px 0px',
                      borderWidth: '1px',
                      borderColor:
                        mode === 'light' ? color._B2CAEA : color._cccc40,
                    }}
                  />
                )}
                {!isMobile && !isTablet && index > 0 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      margin: '10px 0px',
                      borderWidth: '1px',
                      borderColor:
                        mode === 'light' ? color._B2CAEA : color._cccc40,
                    }}
                  />
                )}

                {isTablet && index > 0 && index != 4 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      margin: '10px 0px',
                      borderWidth: '1px',
                      borderColor:
                        mode === 'light' ? color._B2CAEA : color._cccc40,
                    }}
                  />
                )}

                <Grid
                  key={metric.title}
                  item
                  xs={5.75}
                  sm={2.8}
                  md={3}
                  lg={metricConfig.length <= 4 ? 2.7 : 1.6}
                  xl={metricConfig.length <= 4 ? 2.7 : 1.6}
                >
                  {renderKpiBlock(metric)}
                </Grid>
                {index === metricConfig.length - 1 && showKpi && isMdUp && (
                  <div className="desktopDrawerIcon">
                    <KpiDefinitionDrawer
                      title=""
                      ppSubHeadingNote={ppSubHeading}
                      KpiDefinitionList={KpiDefinitionList}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </Grid>
        )}
      </Grid>
    </Styled.MainContainer>
  );
};
export default MetricsOverviewCard;
